import type { QueryClient } from "@tanstack/react-query";

import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";

import type { WorkspacePageDetails } from "~/workspaces/state/workspace-page-details";
import type { GetWorkspacePageDataParams } from "~/workspaces/api/get-workspace-page-data";
import { getWorkspacePageData } from "~/workspaces/api/get-workspace-page-data";
import { useWorkspace } from "~/workspaces/hooks/use-workspace";

function getWorkspacePageDataQueryOptions(
  params: GetWorkspacePageDataParams,
  options?: {
    enabled?: boolean;
    queryClient?: QueryClient;
  },
) {
  const queryClient = options?.queryClient;

  return queryOptions({
    queryKey: ["workspace", "page-data", params],
    enabled: typeof params.pageId !== "undefined" && (options?.enabled ?? true),
    gcTime: 1000 * 60 * 60, // 1 hour
    staleTime: 1000 * 60 * 60, // 1 hour
    retry: false,
    async queryFn() {
      let latestData!: WorkspacePageDetails;

      try {
        for await (const streamChunk of getWorkspacePageData(params)) {
          latestData = streamChunk;

          if (streamChunk.error) {
            if (queryClient) {
              queryClient.setQueryData(
                ["workspace", "page-data", params],
                latestData,
              );
            }
            return latestData;
          }

          if (queryClient) {
            queryClient.setQueryData(
              ["workspace", "page-data", params],
              latestData,
            );
          }
        }
      } catch (error) {
        const errorData: WorkspacePageDetails = {
          error:
            error instanceof Error
              ? error.message
              : "Failed to fetch page details",
        };
        if (queryClient) {
          queryClient.setQueryData(
            ["workspace", "page-data", params],
            errorData,
          );
        }
        return errorData;
      }

      return latestData;
    },
  });
}

function useWorkspacePageData<TSelect = WorkspacePageDetails>(
  params: GetWorkspacePageDataParams,
  options?: {
    enabled?: boolean;
    select?: (data: WorkspacePageDetails) => TSelect;
  },
) {
  const { enabled, select } = options ?? {};
  const queryClient = useQueryClient();
  return useQuery({
    ...getWorkspacePageDataQueryOptions(params, { enabled, queryClient }),
    select,
  });
}

function useWorkspacePageDetailErrors(activePageId?: string) {
  const { selectedWorkspace } = useWorkspace();
  const enabled = !!activePageId;

  const pageDataQuery = useWorkspacePageData(
    {
      workspaceId: selectedWorkspace.id,
      pageId: activePageId ? +activePageId : -1,
    },
    { enabled },
  );

  return {
    data: pageDataQuery.data,
    isLoading: pageDataQuery.isLoading,
    isError: pageDataQuery.isError,
    errorMessage: pageDataQuery.isError
      ? pageDataQuery.error?.message
      : undefined,
    refetch: pageDataQuery.refetch,
  };
}

export {
  getWorkspacePageDataQueryOptions,
  useWorkspacePageData,
  useWorkspacePageDetailErrors,
};
